import { useMemo } from 'react';
import { CalendarClock, Check, Play, SkipForward, Sparkles } from 'lucide-react';
import type { Task } from '@/types';
import { Button, IconButton } from '@/components/ui';
import { useEditorOpenStore } from '@/features/editor/useEditorOpen';
import { useTaskAction } from '@/features/lifecycle/useTaskAction';
import { SkipReasonPopover } from '@/features/lifecycle/SkipReasonPopover';
import { SchedulePopover } from '@/features/inbox/SchedulePopover';
import { formatTimeRange } from '@/lib/time';
import { ringCircumference, ringDashOffset } from '@/lib/ringGeometry';
import { NowProgressBar, NowStatusPill, NowTitle } from './NowShell';
import { progressPct } from './todaySelectors';

export interface NowCardProps {
  task: Task;
  now: Date;
}

/**
 * The hero card on mobile Today (and the desktop Narrator). Mirrors the
 * mockup_v3 NOW card: status pill with pulse, big title, ring + remaining
 * time + linear progress, AI micro-step (when present).
 *
 * Actions are one-tap and act in place: Start flips the task to in_progress
 * (becoming Done, which completes it), Skip and Reschedule sit alongside.
 * Tapping anywhere else on the card opens the editor.
 */
export function NowCard({ task, now }: NowCardProps) {
  const openEditor = useEditorOpenStore((s) => s.openEditor);
  const { start: startTask, complete, snooze } = useTaskAction();

  const start = task.scheduledStart;
  const end = task.scheduledEnd;

  const pct = useMemo(() => (start && end ? progressPct(start, end, now) : 0), [start, end, now]);

  if (!start || !end) return null;

  const remainingMin = Math.max(0, Math.ceil((end.getTime() - now.getTime()) / 60_000));

  const ringSize = 48;
  const ringStroke = 3.5;
  const ringRadius = ringSize / 2 - 4;
  const circumference = ringCircumference(ringRadius);
  const dashOffset = ringDashOffset(ringRadius, pct);

  const isLive = task.status === 'in_progress';

  return (
    <article
      className="pressable cursor-pointer rounded-2xl border border-line-2 bg-card p-4"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        openEditor(task.id);
      }}
    >
      <NowStatusPill className="mb-2">{isLive ? 'In progress' : 'Now'}</NowStatusPill>

      {/* Real button so keyboard/screen-reader users can open the editor —
          the card-level onClick is pointer convenience only. */}
      <NowTitle>
        <button
          type="button"
          onClick={() => openEditor(task.id)}
          className="line-clamp-2 w-full break-words rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
        >
          {task.title}
        </button>
      </NowTitle>

      <div className="mt-3 flex items-center gap-3">
        <svg
          width={ringSize}
          height={ringSize}
          viewBox={`0 0 ${ringSize} ${ringSize}`}
          className="shrink-0"
          aria-hidden
        >
          <circle
            cx={ringSize / 2}
            cy={ringSize / 2}
            r={ringRadius}
            fill="none"
            stroke="var(--rail)"
            strokeWidth={ringStroke}
          />
          <circle
            cx={ringSize / 2}
            cy={ringSize / 2}
            r={ringRadius}
            fill="none"
            stroke="var(--brand)"
            strokeWidth={ringStroke}
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${ringSize / 2} ${ringSize / 2})`}
          />
        </svg>
        <div className="min-w-0 flex-1">
          <div className="font-mono tabular-nums text-body-xl font-medium leading-tight text-t1">
            {formatTimeRange(start, end)}
          </div>
          <div className="mt-0.5 font-mono text-2xs tabular-nums text-t3">
            {remainingMin > 0 ? `${remainingMin}m left · ${Math.round(pct * 100)}%` : 'Time up'}
          </div>
          <NowProgressBar
            pct={pct}
            className="mt-1.5"
            fillClassName="bg-gradient-to-r from-brand to-brand-ink"
          />
        </div>
      </div>

      {(() => {
        const stepText = task.microStep?.firstStep;
        if (!stepText) return null;
        return (
          <div className="mt-3 flex gap-2 rounded-xl border border-brand-line bg-brand-soft px-3 py-2">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden />
            <p className="text-caption leading-snug text-brand-ink">{stepText}</p>
          </div>
        );
      })()}

      {/* flex-wrap + no min-w-0: the primary button grows (flex-1) but must never
          shrink below its label ("Start" was clipping at large text sizes in the
          320px rail) — overflow wraps the trailing controls instead. */}
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {isLive ? (
          <Button
            size="md"
            variant="primary"
            className="flex-1"
            leadingIcon={<Check className="h-4 w-4" aria-hidden />}
            onClick={() => complete(task.id)}
          >
            Done
          </Button>
        ) : (
          <Button
            size="md"
            variant="primary"
            className="flex-1"
            leadingIcon={<Play className="h-4 w-4" aria-hidden />}
            onClick={() => startTask(task.id)}
          >
            Start
          </Button>
        )}
        <SkipReasonPopover
          task={task}
          trigger={
            <Button
              size="md"
              variant="secondary"
              aria-label="Skip task"
              className="px-2.5"
              leadingIcon={<SkipForward className="h-4 w-4" aria-hidden />}
            >
              Skip
            </Button>
          }
        />
        <Button
          size="md"
          variant="secondary"
          aria-label={isLive ? 'Add 15 minutes' : 'Snooze 15 minutes'}
          className="px-2.5 font-mono"
          onClick={() => snooze(task.id)}
        >
          +15m
        </Button>
        <SchedulePopover
          task={task}
          trigger={
            <IconButton
              label="Reschedule"
              variant="secondary"
              size="lg"
              icon={<CalendarClock className="h-4 w-4" aria-hidden />}
            />
          }
        />
      </div>
    </article>
  );
}
